'use client'

import { Select } from '@/components/ui/select'
import { useIsMobileOrTabletClient } from '@/hooks/common/use-device-type'
import { cn } from '@/lib/utils'
import { scrollToProductSection } from '@/lib/utils/scroll-utils'
import { TCollectionData } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useShallow } from 'zustand/react/shallow'
import {
  COLLECTION_SORT_LABELS,
  ECollectionSort,
  shouldHideSorting,
} from '../../../constants'
import { useCollectionData } from '../../../hooks/use-collection-data'
import { useCollectionFilterStoreV2 } from '../../../store/collection-filter-store-v2'

const SORT_LABEL_FALLBACKS: Record<string, string> = {
  default: 'Mặc định',
  bestseller: 'Bán chạy',
  new: 'Mới nhất',
  price_asc: 'Giá thấp đến cao',
  price_desc: 'Giá cao đến thấp',
  percent: '% Giảm giá nhiều',
}

type Props = {
  title?: string
  serverData: TCollectionData
  initialFilters?: Record<string, string | string[] | number | undefined>
  className?: string
}

export const FilterSortBy: React.FC<Props> = ({
  serverData,
  initialFilters,
  title,
  className,
}) => {
  const { data, isLoading } = useCollectionData({ serverData, initialFilters })
  const isMobile = useIsMobileOrTabletClient()
  const productCount =
    data?.pages?.[data.pages.length - 1]?.pagination?.total ?? 0

  const { setSort } = useCollectionFilterStoreV2(
    useShallow((state) => ({
      setSort: state.setSort,
    }))
  )
  const originalSlug = useCollectionFilterStoreV2((state) => state.originalSlug)
  const hideSorting = shouldHideSorting(originalSlug)
  const searchParams = useSearchParams()
  const t = useTranslations('collection_page')

  const handleFilterChange = (value: string) => {
    setSort(value)
    scrollToProductSection(200)
  }

  const sort = searchParams.get('sort')

  const sortOptions = Object.entries(COLLECTION_SORT_LABELS).map(
    ([key, label]) => {
      const translated = t(`sort_label.${label}`)
      return {
        value: key,
        label:
          translated && !translated.startsWith('sort_label.')
            ? translated
            : SORT_LABEL_FALLBACKS[label] || label,
      }
    }
  )

  const sortByLabel = (() => {
    const text = t('title.sort_by')
    return text && !text.includes('title.') ? text : 'Sắp xếp theo'
  })()

  // If hiding sorting but has title, still render title section
  if (hideSorting && !title) {
    return null
  }

  return (
    <div className={cn('', className)}>
      <div className='flex w-full items-center justify-between bg-light px-5 pb-4 pt-3.5 max-lg:shadow-[0_4px_12px_0_rgba(0,0,0,0.08)] lg:p-0'>
        {title ? (
          <h1 className='font-criteria text-[32px] font-medium uppercase leading-13 text-neutral-900'>
            {title}
          </h1>
        ) : (
          <p
            className={cn(
              'font-sans text-sm leading-4.5 text-neutral-500',
              isMobile ? 'block' : 'hidden'
            )}
          >
            {t('title.count_result', {
              count: isLoading ? '...' : productCount || 0,
            })}
          </p>
        )}

        {!hideSorting && (
          <div className='flex items-center gap-3'>
            <span className='whitespace-nowrap font-sans text-sm font-medium text-neutral-800 max-lg:hidden'>
              {sortByLabel}
            </span>
            <Select
              options={sortOptions}
              placeholder={sortByLabel}
              className={{
                trigger:
                  'h-auto w-[160px] rounded-none border-0 border-b border-neutral-900 bg-transparent px-0 py-2.5 text-sm font-medium text-neutral-900 shadow-none hover:border-neutral-900 hover:bg-transparent focus:outline-none data-[placeholder]:text-neutral-900 data-[state=open]:border-neutral-900 data-[state=open]:bg-transparent lg:w-[180px]',
              }}
              onValueChange={handleFilterChange}
              defaultValue={sort || ECollectionSort.DEFAULT}
            />
          </div>
        )}
      </div>
    </div>
  )
}
